import UniversalWebView from '@/components/UniversalWebView'
import type { OpenLinkResult } from '@/components/universalWebView/types'
import { getAuthToken } from '@/lib/api/authToken'
import { handleClickToPay } from '@/modules/handleClickToPay'
import { useAppSelector } from '@/redux/store'
import { PlanCode } from '@/typings/domain'
import { get } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'

import { artifactPageToken, artifactPageUrl } from './artifactUrl'
import useExportFile from './useExportFile'

const NEXT_BASE_URL = process.env.EXPO_PUBLIC_NEXT_BASE_URL ?? ''

/** nextfe's page for a file Jiri delivered, with its 分享, 升级 and 下载 handed to the App. */
export default function ArtifactWebView({ token }: { token: string }) {
  const currentUser = useAppSelector(state => state.currentUser.user)
  const exportFile = useExportFile()
  const [pageToken, setPageToken] = useState(token)

  const uri = useMemo(() => artifactPageUrl(pageToken, NEXT_BASE_URL, getAuthToken()), [pageToken])

  // Another artifact stays here, rebuilt like the first one so it keeps the App's login; a link on the same page
  // (an anchor) loads as is, and anything else leaves the App.
  const openLink = useCallback(
    (url: string): OpenLinkResult => {
      const linkToken = artifactPageToken(url, NEXT_BASE_URL)
      if (!linkToken) return 'browser'
      if (linkToken === pageToken) return 'webview'
      setPageToken(linkToken)
      return 'handled'
    },
    [pageToken]
  )

  const handleMessage = useCallback(
    (data: { event: string; details: unknown }) => {
      if (data.event === 'CLICK_TO_PAY') {
        const recommendedPlanCode = get(data.details, 'recommendedPlanCode') as PlanCode | undefined
        handleClickToPay({ recommendedPlanCode, currentUser: currentUser ?? undefined })
        return
      }

      if (data.event === 'EXPORT_REPORTS') {
        const url = get(data.details, 'url', '')
        if (url) return exportFile(url, get(data.details, 'label', ''))
      }
    },
    [currentUser, exportFile]
  )

  return (
    <UniversalWebView
      source={{ uri }}
      className="flex-1 bg-background-muted"
      onMessage={handleMessage}
      onOpenLink={openLink}
    />
  )
}
